import { intlLocale, t } from "../i18n";

/**
 * Время и даты для показа. Длительности — секунды (контракт), даты — ISO в UTC,
 * показываются в часовом поясе человека.
 */

/** 5 → "0:05", 754 → "12:34", 3723 → "1:02:03". Отрицательное и NaN — как ноль. */
export function formatClock(totalSeconds: number): string {
  const safe = Number.isFinite(totalSeconds) && totalSeconds > 0 ? Math.floor(totalSeconds) : 0;
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  const ss = String(seconds).padStart(2, "0");
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, "0")}:${ss}`;
  return `${minutes}:${ss}`;
}

/** Длина серии в списке: "12 min". Меньше минуты — всё равно "1 min": ноль минут выглядит поломкой. */
export function formatMinutes(totalSeconds: number): string {
  const minutes = Math.max(1, Math.round(totalSeconds / 60));
  return t("episode.minutes", { min: minutes });
}

/** "Oct 4, 2026" в часовом поясе человека; кривой пояс не роняет экран. */
export function formatDate(iso: string, timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" };
  try {
    return new Intl.DateTimeFormat(intlLocale(), timeZone ? { ...options, timeZone } : options).format(date);
  } catch {
    return new Intl.DateTimeFormat(intlLocale(), options).format(date);
  }
}

/**
 * Когда выйдет серия: "Sun, Oct 4, 9:00 PM" — в часовом поясе подписчика, потому что
 * расписание считается по его часам. Кривой пояс — время устройства, а не пустота.
 */
export function formatSchedule(iso: string, timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const options: Intl.DateTimeFormatOptions = {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  };
  try {
    return new Intl.DateTimeFormat(intlLocale(), timeZone ? { ...options, timeZone } : options).format(date);
  } catch {
    return new Intl.DateTimeFormat(intlLocale(), options).format(date);
  }
}

/** "Sep 21" — дата выхода в списке серий, без года, если год текущий. */
export function formatShortDate(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const sameYear = date.getFullYear() === now.getFullYear();
  return new Intl.DateTimeFormat(intlLocale(), {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  }).format(date);
}

/** Часовой пояс устройства; на старых браузерах — UTC, чтобы регистрация не падала. */
export function deviceTimeZone(): string {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone && zone.length > 0 ? zone : "UTC";
  } catch {
    return "UTC";
  }
}

/** Первая буква имени для круглого аватара без картинки. */
export function initial(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  const first = Array.from(trimmed)[0] ?? "?";
  return first.toUpperCase();
}
